export type AuditKind = 'User' | 'User group' | 'Azure AD group' | 'Other target';
export type RequiredFilter = 'all' | 'required' | 'optional';

export interface AuditRow {
  id: string;
  kind: AuditKind;
  journeyMapId: number | null;
  journeyMapName: string;
  assignee: string;
  assignedBy: string;
  isRequired: boolean;
  dueDate: string;
  status: string;
  notifiedAt: string;
  isActive: boolean;
}

const TARGET_KIND: Record<string, AuditKind> = {
  user_group: 'User group',
  azure_ad_group: 'Azure AD group',
};

const day = (value?: string | null) => (value ? String(value).slice(0, 10) : '');

/** One audit row per individual assignment and per group/role target, newest map first by name. */
export function buildAuditRows(args: {
  assignments: any[];
  targets: any[];
  userGroupNames: Record<string, string>;
  azureGroupNames: Record<string, string>;
}): AuditRow[] {
  const rows: AuditRow[] = [];

  for (const a of args.assignments) {
    rows.push({
      id: `assignment:${a.id}`,
      kind: 'User',
      journeyMapId: a.journey_map ?? null,
      journeyMapName: a.journey_map_name || `Journey ${a.journey_map}`,
      assignee: a.user_email || `User ${a.user}`,
      assignedBy: a.assigned_by_email || '',
      isRequired: !!a.is_required,
      dueDate: day(a.due_date),
      status: a.status_name || (a.completed_date ? 'Completed' : ''),
      notifiedAt: day(a.notified_at),
      isActive: a.is_active !== false,
    });
  }

  for (const t of args.targets) {
    const ref = String(t.target_ref_id ?? '');
    const kind = TARGET_KIND[t.target_type] ?? 'Other target';
    const names = t.target_type === 'azure_ad_group' ? args.azureGroupNames : args.userGroupNames;
    rows.push({
      id: `target:${t.id}`,
      kind,
      journeyMapId: t.journey_map ?? null,
      journeyMapName: t.journey_map_name || `Journey ${t.journey_map}`,
      assignee: names[ref] || (t.target_type ? `${t.target_type}:${ref}` : ref),
      assignedBy: t.assigned_by_email || '',
      isRequired: !!t.is_required,
      dueDate: day(t.due_date),
      status: t.is_active === false ? 'Inactive' : 'Active',
      notifiedAt: '',
      isActive: t.is_active !== false,
    });
  }

  return rows.sort(
    (a, b) => a.journeyMapName.localeCompare(b.journeyMapName) || a.assignee.localeCompare(b.assignee)
  );
}

export function filterAuditRows(
  rows: AuditRow[],
  filters: { search: string; journeyMapId: number | 'all'; required: RequiredFilter; kind: AuditKind | 'all' }
): AuditRow[] {
  const q = filters.search.trim().toLowerCase();

  return rows.filter((row) => {
    if (filters.journeyMapId !== 'all' && row.journeyMapId !== filters.journeyMapId) return false;
    if (filters.required === 'required' && !row.isRequired) return false;
    if (filters.required === 'optional' && row.isRequired) return false;
    if (filters.kind !== 'all' && row.kind !== filters.kind) return false;
    if (!q) return true;
    return [row.journeyMapName, row.assignee, row.assignedBy, row.status].some((v) =>
      v.toLowerCase().includes(q)
    );
  });
}
